import React from 'react';
import { useVatMode } from '../../hooks/useVatMode';

/** The Variance figure's sign and colour — ONE rule for the banner and the inline form. */
function varianceTone(summary) {
  const variance = summary.variance || 0;
  const varianceColor = variance > 0 ? 'text-red-600 dark:text-red-400' : variance < 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400';
  const variancePrefix = variance > 0 ? '+' : '';
  return { variance, varianceColor, variancePrefix };
}

/**
 * The SAME three money figures as the banner's cost row, as one compact inline
 * run for the hires TABLE view's status-tab line (owner, 2026-09-27: the block
 * *"should either go on this line, right aligned … or this line"* rather than
 * on a row of its own). Muted label + value pairs — the page chrome's
 * INFORMATION weight (`ui/CLAUDE.md`, the three weights), never a control.
 * Variance keeps the banner's colour rule (`varianceTone`). Renders nothing
 * until the summary has loaded.
 *
 * 🔻 The Table view has NO count boxes (owner: *"we dont need the top boxes as
 * they take up LOADS of space"*) — its status tabs carry counts. 🔀 Since
 * 2026-10-08 the boxes count the SAME `utils/hireBuckets.js` buckets as the
 * tabs and the Kanban columns (`HiresPage` passes `counts`; Ending Soon carries
 * the overdue figure as a red sub-line). The boxes use the UNFILTERED list and
 * the tabs the search-filtered one, so with no search they agree exactly.
 */
export function CostSummaryInline({ summary, className = '' }) {
  const { formatMoney } = useVatMode();
  if (!summary) return null;
  const { variance, varianceColor, variancePrefix } = varianceTone(summary);
  const pair = (label, value, valueClass, testId) => (
    <span className="inline-flex items-baseline gap-1 whitespace-nowrap" data-testid={testId}>
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${valueClass}`}>{value}</span>
    </span>
  );
  return (
    <div className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 ${className}`.trim()} data-testid="cost-summary-inline">
      {pair('Expected', formatMoney(summary.expected_total), 'text-gray-800 dark:text-gray-200', 'cost-inline-expected')}
      {pair('Current', formatMoney(summary.current_total), 'text-gray-800 dark:text-gray-200', 'cost-inline-current')}
      {pair('Variance', `${variancePrefix}${formatMoney(Math.abs(variance))}`, varianceColor, 'cost-inline-variance')}
    </div>
  );
}

/**
 * Cost summary banner for the hires pages.
 * Shows active/overdue/upcoming counts and expected/current/variance costs.
 * Rendered in every hires view EXCEPT the Table (see `CostSummaryInline`).
 */
function CostSummary({ summary, counts, loading }) {
  const { formatMoney } = useVatMode();
  if (loading || !summary) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 animate-pulse">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded" />
          ))}
        </div>
      </div>
    );
  }

  // `counts` (from `HiresPage`, `utils/hireBuckets.js#bucketHires`) makes the
  // boxes the SAME buckets as the Kanban columns and Table tabs (owner
  // 2026-10-08). Without it, the server's raw `hire_status` counts as before.
  const bucketed = counts != null;
  const stats = [
    {
      label: 'Upcoming',
      value: bucketed ? counts.upcoming : (summary.upcoming_count || 0),
      color: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-50 dark:bg-amber-900/30',
      border: 'border-amber-200 dark:border-amber-700',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      label: 'On Hire',
      value: bucketed ? counts.onHire : (summary.active_count || 0),
      color: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-50 dark:bg-blue-900/30',
      border: 'border-blue-200 dark:border-blue-700',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      label: bucketed ? 'Ending Soon' : 'Overdue',
      value: bucketed ? counts.endingSoon : (summary.overdue_count || 0),
      // Overdue stays visible as its own figure inside Ending Soon.
      sub: bucketed && counts.overdue > 0 ? `${counts.overdue} overdue` : null,
      color: 'text-red-600 dark:text-red-400',
      bg: 'bg-red-50 dark:bg-red-900/30',
      border: 'border-red-200 dark:border-red-700',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      ),
    },
    {
      label: 'Off-Hired',
      value: bucketed ? counts.offHired : (summary.off_hired_count || 0),
      color: 'text-gray-600 dark:text-gray-400',
      bg: 'bg-gray-50 dark:bg-gray-700/50',
      border: 'border-gray-200 dark:border-gray-600',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ),
    },
  ];

  const { variance, varianceColor, variancePrefix } = varianceTone(summary);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6" data-testid="cost-summary">
      {/* Status counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 border-b border-gray-100 dark:border-gray-700">
        {stats.map(stat => (
          <div
            key={stat.label}
            className={`${stat.bg} ${stat.border} border rounded-lg p-3 flex items-center gap-3`}
            data-testid={`stat-${stat.label.toLowerCase().replace(/[- ]/g, '_')}`}
          >
            <div className={`${stat.color} flex-shrink-0`}>{stat.icon}</div>
            <div>
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">{stat.label}</div>
              {stat.sub && (
                <div className="text-xs font-semibold text-red-600 dark:text-red-400" data-testid="stat-overdue-sub">{stat.sub}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Cost summary — 3-col on all breakpoints; compact on mobile to fit in one row */}
      <div className="grid grid-cols-3 gap-2 md:gap-4 p-3 md:p-4">
        <div className="text-center">
          <div className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Expected</div>
          <div className="text-base md:text-xl font-bold text-gray-800 dark:text-gray-200 mt-0.5 md:mt-1">{formatMoney(summary.expected_total)}</div>
        </div>
        <div className="text-center">
          <div className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Current</div>
          <div className="text-base md:text-xl font-bold text-gray-800 dark:text-gray-200 mt-0.5 md:mt-1">{formatMoney(summary.current_total)}</div>
        </div>
        <div className="text-center">
          <div className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Variance</div>
          <div className={`text-base md:text-xl font-bold mt-0.5 md:mt-1 ${varianceColor}`}>
            {variancePrefix}{formatMoney(Math.abs(variance))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CostSummary;
